import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  accessFromAvailability,
  DeveloperAccess,
  NO_ACCESS,
} from '@/config/app';
import { getSandboxAvailability } from '@/services/api/integrationAccess';
import { useAuth } from './AuthContext';

interface IntegrationAccessValue {
  /** What the client may use — NO_ACCESS until known. */
  access: DeveloperAccess;
  /** False while the answer for the current client is still loading. */
  ready: boolean;
}

const IntegrationAccessContext = createContext<IntegrationAccessValue>({
  access: NO_ACCESS,
  ready: false,
});

/**
 * Asks the backend once per client (per app load) which integration APIs it
 * may use — GET /v1/integration/sandbox-availability — and shares the answer
 * with the sidebar, the route gates and the Developer page. Until it answers
 * nothing is shown; an error leaves everything hidden.
 */
export const IntegrationAccessProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { client, token, isInitializing } = useAuth();
  const clientId = token ? (client?.id ?? null) : null;
  const [loaded, setLoaded] = useState<{
    clientId: number;
    access: DeveloperAccess;
  } | null>(null);

  // One request per signed-in client (a new sign-in asks again).
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    getSandboxAvailability()
      .then((availability) => {
        if (!cancelled) {
          setLoaded({ clientId, access: accessFromAvailability(availability) });
        }
      })
      .catch((err) => {
        console.error('Failed to load integration API access:', err);
        if (!cancelled) setLoaded({ clientId, access: NO_ACCESS });
      });
    return () => {
      cancelled = true;
    };
  }, [token, clientId]);

  const value: IntegrationAccessValue = clientId
    ? loaded?.clientId === clientId
      ? { access: loaded.access, ready: true }
      : { access: NO_ACCESS, ready: false }
    : // Signed out, or the session restore ended without a client: nothing
      // to ask — hidden, but settled. Still restoring: keep waiting.
      { access: NO_ACCESS, ready: !token || !isInitializing };

  return (
    <IntegrationAccessContext.Provider value={value}>
      {children}
    </IntegrationAccessContext.Provider>
  );
};

export const useIntegrationAccess = (): IntegrationAccessValue =>
  useContext(IntegrationAccessContext);
